/**
 * Section component - Provides consistent spacing and layout structure
 *
 * A foundational layout primitive that ensures consistent spacing,
 * alignment, and responsive behavior across different page sections.
 */

import { Box, Container, SxProps } from '@mui/material';
import { ReactNode, forwardRef } from 'react';
import { useAppTheme } from '../../app/providers/ThemeProvider';

export interface SectionProps {
  /** Content to render within the section */
  children: ReactNode;
  /** Variant controlling padding and visual hierarchy */
  variant?: 'default' | 'compact' | 'spacious' | 'hero';
  /** Background color variant */
  background?: 'default' | 'paper' | 'primary' | 'secondary';
  /** Maximum width constraint */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  /** Whether to include horizontal padding */
  disableGutters?: boolean;
  /** Custom sx props for styling */
  sx?: SxProps;
  /** HTML element type */
  component?: React.ElementType;
  /** Additional props passed to the root element */
  [key: string]: unknown;
}

/**
 * Section component providing consistent layout structure
 */
export const Section = forwardRef<HTMLElement, SectionProps>(
  (
    {
      children,
      variant = 'default',
      background = 'default',
      maxWidth = 'lg',
      disableGutters = false,
      sx,
      component = 'section',
      ...props
    },
    ref,
  ) => {
    const theme = useAppTheme();
    const { customTokens: tokens } = theme;

    // Padding variants
    const paddingVariants = {
      default: {
        py: { xs: 4, md: 6 },
      },
      compact: {
        py: { xs: 3, md: 4 },
      },
      spacious: {
        py: { xs: 6, md: 10 },
      },
      hero: {
        py: { xs: 8, md: 12 },
      },
    };

    // Background variants
    const backgroundVariants = {
      default: {
        backgroundColor: 'background.default',
      },
      paper: {
        backgroundColor: 'background.paper',
      },
      primary: {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      },
      secondary: {
        backgroundColor: 'secondary.main',
        color: 'secondary.contrastText',
      },
    };

    return (
      <Box
        component={component as React.ElementType}
        ref={ref}
        sx={[
          {
            position: 'relative',
            width: '100%',
            ...paddingVariants[variant as keyof typeof paddingVariants],
            ...backgroundVariants[background as keyof typeof backgroundVariants],
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...props}
      >
        {maxWidth !== false ? (
          <Container
            maxWidth={maxWidth === false ? false : (maxWidth as any)}
            disableGutters={Boolean(disableGutters)}
            sx={{ height: '100%' }}
          >
            {children as React.ReactNode}
          </Container>
        ) : (
          <Box
            sx={{
              px: (disableGutters ?? false) ? 0 : { xs: 3, md: 4 },
              height: '100%',
            }}
          >
            {children as React.ReactNode}
          </Box>
        )}
      </Box>
    );
  },
);

Section.displayName = 'Section';

export default Section;
